import { env } from "@/lib/env";
import { createLeader, type BroadcastChannelLike, type LeaderHandle, type LockManagerLike } from "./leader";
import {
  REALTIME_EVENTS,
  TERMINAL_EVENTS,
  isRealtimeEvent,
  type RealtimeEventName,
  type RealtimeFrame,
} from "./realtime-events";

/**
 * ONE EventSource for the whole BROWSER.
 *
 * WHY NOT ONE PER TAB. The server caps a person at five concurrent streams and closes the OLDEST
 * when a sixth opens (`event: overflow`): six tabs of this application would evict each other,
 * and each one costs the server a socket and a Redis subscription for frames its siblings already
 * have. The tabs elect a leader (`leader.ts`); the leader holds the socket and relays every frame
 * to the others, and when it goes away the next tab takes over and RESUMES from the last id it saw.
 *
 * RECONNECTION, AND THE THREE CASES THE BROWSER DOES NOT HANDLE.
 *   1. `EventSource` retries a dropped socket by itself, but NOT a non-200 answer — a 401 after
 *      the session expired, a 403 for a support actor, a 429 at the connect limit all move it to
 *      `CLOSED` for good. Those are exactly the cases where silence looks like "nothing is
 *      happening", so this reopens them with a jittered backoff.
 *   2. A socket can die without the browser noticing (laptop lid, a proxy dropping an idle
 *      connection): nothing arrives and no error fires. The WATCHDOG measures silence against the
 *      `heartbeatSeconds` the server announced — the heartbeat is a named `ping` event precisely
 *      so a client can see it — and reopens after two missed beats.
 *   3. A tab that was asleep. `visibilitychange`, `focus` and `online` reopen immediately and
 *      reset the backoff: the person is looking at the screen now.
 *
 * RESUME. Every business frame carries the id of its entry in the server's replay log. The client
 * remembers the last one and passes it back (`?since=`, and the browser's own reconnect sends
 * `Last-Event-ID`), so a reconnection replays what was missed instead of losing it. When the
 * server cannot prove the resume is complete it sends `resync`, and the subscriber refetches.
 *
 * WHAT IT NEVER DOES: parse a frame into application state. A subscriber gets ids and decides what
 * to refetch (`realtime-events.ts`).
 */

type Listener = (frame: RealtimeFrame) => void;

/** Backoff for the cases `EventSource` will not retry on its own (§13: 3 s → 30 s). */
const RETRY_BASE_MS = 3_000;
const RETRY_MAX_MS = 30_000;
/** After this many consecutive failures the stream is unavailable, not flaky. */
const MAX_ATTEMPTS = 8;
/** Until `ready` says otherwise. */
const DEFAULT_HEARTBEAT_SECONDS = 25;
/** Silence tolerated beyond two heartbeats, for a slow network rather than a dead socket. */
const WATCHDOG_GRACE_MS = 5_000;

export type RealtimeStatus =
  | "idle"
  | "connecting"
  | "open"
  | "retrying"
  | "stopped"
  /** Another tab of this browser holds the stream; frames arrive from it. */
  | "follower";

interface StatusListener {
  (status: RealtimeStatus): void;
}

export interface RealtimeClientOptions {
  /** Test seams; production reads the globals. */
  readonly locks?: LockManagerLike | null;
  readonly channelFactory?: (name: string) => BroadcastChannelLike | null;
  readonly baseUrl?: string;
}

class RealtimeClient {
  private source: EventSource | undefined;
  private readonly listeners = new Map<RealtimeEventName, Set<Listener>>();
  private readonly statusListeners = new Set<StatusListener>();
  private status: RealtimeStatus = "idle";
  private attempts = 0;
  private retryTimer: ReturnType<typeof setTimeout> | undefined;
  private watchdog: ReturnType<typeof setTimeout> | undefined;
  private heartbeatSeconds = DEFAULT_HEARTBEAT_SECONDS;
  /** Where this browser stands in the server's replay log. */
  private lastEventId: string | null = null;
  /** Set while the application wants a stream; `stop()` clears it so a pending retry dies. */
  private wanted = false;
  private leader: LeaderHandle | undefined;
  private wakeBound = false;
  private readonly options: RealtimeClientOptions;

  constructor(options: RealtimeClientOptions = {}) {
    this.options = options;
  }

  /** Opens the stream (or joins the tab that holds it). Idempotent. */
  start(): void {
    this.wanted = true;
    this.bindWakeEvents();
    if (this.leader) {
      if (this.leader.isLeader() && !this.source) this.open();
      return;
    }
    this.leader = createLeader({
      channelName: "altek.realtime",
      lockName: "altek.realtime.leader",
      onLead: () => {
        if (this.wanted) this.open();
      },
      onFollow: () => {
        this.closeSocket();
        if (this.wanted) this.setStatus("follower");
      },
      onMessage: (message) => this.onLeaderMessage(message),
      ...(this.options.locks !== undefined ? { locks: this.options.locks } : {}),
      ...(this.options.channelFactory ? { channelFactory: this.options.channelFactory } : {}),
    });
    if (!this.leader.isLeader()) {
      this.setStatus("follower");
      // Ask whoever leads where things stand, so a tab opened mid-session is not blank.
      this.leader.publish({ kind: "hello", tabId: this.leader.tabId });
    }
  }

  /** Closes the stream, cancels any pending retry and leaves the election. Called on sign-out. */
  stop(): void {
    this.halt("stopped");
  }

  /**
   * DESTEK OTURUMU: akış BİLEREK kapalı (20 Ekim 2026) — `stop()` gibi kapatır ama durumu `idle` bırakır.
   *
   * Sunucu destek aktörünün akışını 403'le reddediyor (başkasının bildirimleri yöneticiye akmaz). Eskiden
   * istemci bunu kopukluk sanıp geri çekilerek yeniden deniyor ve kabuk «Canlı bağlantı yok» uyarısını
   * gösteriyordu; oysa bozuk bir şey yok. `idle` «açılmadı» demek: `RealtimeIndicator` susar.
   */
  standby(): void {
    this.halt("idle");
  }

  private halt(next: RealtimeStatus): void {
    this.wanted = false;
    this.attempts = 0;
    this.clearRetry();
    this.clearWatchdog();
    this.closeSocket();
    this.leader?.stop();
    this.leader = undefined;
    this.setStatus(next);
  }

  /** Reconnects now, whatever the backoff says — the person asked. */
  reconnect(): void {
    this.attempts = 0;
    this.clearRetry();
    if (!this.wanted) this.wanted = true;
    if (!this.leader) {
      this.start();
      return;
    }
    if (!this.leader.isLeader()) return;
    this.closeSocket();
    this.open();
  }

  /** Subscribes to one event. Returns the unsubscribe function. */
  on(event: RealtimeEventName, listener: Listener): () => void {
    const set = this.listeners.get(event) ?? new Set<Listener>();
    set.add(listener);
    this.listeners.set(event, set);

    /**
     * Bind the name on the LIVE socket too, not only on the next one: `EventSource` dispatches by
     * event name, and every screen reached by navigation subscribes after the shell already
     * opened the stream.
     */
    if (this.source) this.bind(this.source, event);

    return () => {
      set.delete(listener);
      if (set.size === 0) this.listeners.delete(event);
    };
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    listener(this.status);
    return () => this.statusListeners.delete(listener);
  }

  currentStatus(): RealtimeStatus {
    return this.status;
  }

  /** What a takeover resumes from; also what the test asserts the URL carried. */
  currentEventId(): string | null {
    return this.lastEventId;
  }

  /* ------------------------------------------------------------------ internals */

  private open(): void {
    this.clearRetry();
    this.setStatus(this.attempts === 0 ? "connecting" : "retrying");

    const base = this.options.baseUrl ?? env.apiBaseUrl;
    const url = this.lastEventId
      ? `${base}/notifications/stream?since=${encodeURIComponent(this.lastEventId)}`
      : `${base}/notifications/stream`;
    // `withCredentials` is what carries the HttpOnly session cookie cross-origin in dev, where
    // the SPA is on :3071 and the API on :3070. Without it the stream is anonymous and 401s.
    const source = new EventSource(url, { withCredentials: true });
    this.source = source;

    source.addEventListener("open", () => {
      this.attempts = 0;
      this.setStatus("open");
      this.armWatchdog();
    });

    /**
     * EVERY name in the vocabulary is bound, not only the ones somebody subscribed to.
     *
     * `EventSource` dispatches by event name, so an unbound name arrives nowhere — and this client
     * needs the frames anyway even when no screen wants them: the `id` is what a reconnect resumes
     * from (an unbound frame would be replayed a second time), any frame resets the watchdog, and
     * a follower tab may be the one that wanted it.
     */
    for (const event of REALTIME_EVENTS) this.bind(source, event);

    source.addEventListener("error", () => {
      // `CONNECTING` means the browser is retrying on its own — leave it alone, or two reconnect
      // loops race and the server sees a connection storm from one tab.
      if (source.readyState === EventSource.CONNECTING) {
        this.setStatus("retrying");
        return;
      }
      this.scheduleRetry();
    });
  }

  /**
   * Attaches one native listener per event name. Called again for a name subscribed to AFTER the
   * stream opened, because `EventSource` dispatches by name.
   */
  private readonly bound = new WeakMap<EventSource, Set<string>>();

  private bind(source: EventSource, event: RealtimeEventName): void {
    const names = this.bound.get(source) ?? new Set<string>();
    if (names.has(event)) return;
    names.add(event);
    this.bound.set(source, names);

    source.addEventListener(event, (message) => {
      const frame = message as MessageEvent<string>;
      this.onFrame(event, frame.data, frame.lastEventId || null);
    });
  }

  /** A frame from the socket (leader) — remembered, dispatched locally and relayed. */
  private onFrame(event: RealtimeEventName, raw: string, id: string | null): void {
    this.armWatchdog();
    if (id) this.lastEventId = id;

    const data = parse(raw);
    if (data === null) return;

    if (event === "ready") {
      const heartbeat = Number((data as { heartbeatSeconds?: unknown }).heartbeatSeconds);
      if (Number.isFinite(heartbeat) && heartbeat > 0) this.heartbeatSeconds = heartbeat;
      this.armWatchdog();
    }

    /**
     * The customer changed under this session: the ids in hand belong to the OLD customer's replay
     * log and resuming with one would be refused anyway. Forget it, then reconnect into the new
     * customer — the server closed this socket as it sent the frame.
     */
    if (event === "session.tenant_changed") {
      this.lastEventId = null;
      this.dispatch(event, data);
      this.leader?.publish({ kind: "frame", event, data, id: null });
      this.attempts = 0;
      window.setTimeout(() => {
        if (this.wanted && this.leader?.isLeader()) this.open();
      }, 0);
      return;
    }

    /**
     * The three frames the server sends immediately before closing the socket itself. Retrying
     * into them is worse than useless: `session.revoked` and `expired` are 401 loops, and
     * `overflow` would evict the tab the person is actually looking at, which would evict this
     * one back — two tabs taking turns killing each other.
     */
    if (TERMINAL_EVENTS.includes(event)) this.wanted = false;

    this.dispatch(event, data);
    if (event !== "ping") this.leader?.publish({ kind: "frame", event, data, id });
  }

  /** A frame (or a status) relayed by the leader of another tab. */
  private onLeaderMessage(message: { kind: string } & Record<string, unknown>): void {
    if (message.kind === "hello") {
      if (this.leader?.isLeader()) {
        this.leader.publish({ kind: "status", status: this.status, lastEventId: this.lastEventId });
      }
      return;
    }
    if (message.kind === "status") {
      if (this.leader?.isLeader()) return;
      if (typeof message.lastEventId === "string") this.lastEventId = message.lastEventId;
      this.setStatus(message.status === "open" ? "follower" : (message.status as RealtimeStatus));
      return;
    }
    if (message.kind !== "frame") return;
    const event = message.event;
    if (typeof event !== "string" || !isRealtimeEvent(event)) return;
    // A follower keeps the id too: it is what it resumes from if it becomes the leader.
    if (typeof message.id === "string") this.lastEventId = message.id;
    this.dispatch(event, (message.data ?? {}) as Record<string, unknown>);
  }

  private dispatch(event: RealtimeEventName, data: Record<string, unknown>): void {
    const frame: RealtimeFrame = { event, data };
    for (const listener of this.listeners.get(event) ?? []) {
      try {
        listener(frame);
      } catch {
        // One subscriber's failure must not silence the others on the same frame.
      }
    }
  }

  /**
   * Silence longer than two heartbeats is a socket the browser has not noticed is dead. Reopening
   * is cheap (the resume replays what was missed); waiting is a person who stops being told
   * anything and cannot tell.
   */
  private armWatchdog(): void {
    this.clearWatchdog();
    if (!this.wanted) return;
    const silence = this.heartbeatSeconds * 2 * 1000 + WATCHDOG_GRACE_MS;
    this.watchdog = setTimeout(() => {
      this.watchdog = undefined;
      if (!this.wanted || !this.leader?.isLeader()) return;
      this.closeSocket();
      this.attempts = 0;
      this.open();
    }, silence);
  }

  private clearWatchdog(): void {
    if (this.watchdog) clearTimeout(this.watchdog);
    this.watchdog = undefined;
  }

  private clearRetry(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
  }

  private closeSocket(): void {
    this.clearWatchdog();
    this.source?.close();
    this.source = undefined;
  }

  private scheduleRetry(): void {
    this.closeSocket();

    if (!this.wanted) {
      this.setStatus("stopped");
      return;
    }

    this.attempts += 1;
    if (this.attempts > MAX_ATTEMPTS) {
      // Not silent: `useRealtimeStatus` shows it and offers `reconnect()`.
      this.setStatus("stopped");
      return;
    }

    // Jitter, because a deploy drops every open stream at once and an un-jittered backoff brings
    // them all back in the same second.
    const base = Math.min(RETRY_BASE_MS * 2 ** (this.attempts - 1), RETRY_MAX_MS);
    const delay = base / 2 + Math.random() * (base / 2);
    this.setStatus("retrying");
    this.retryTimer = setTimeout(() => {
      this.retryTimer = undefined;
      if (this.wanted && this.leader?.isLeader()) this.open();
    }, delay);
  }

  /**
   * A tab that comes back to the front tries again AT ONCE, whatever the backoff was counting to:
   * the person is looking now, and a NAT-shared 429 or a sleeping laptop is exactly the state this
   * recovers from.
   */
  private bindWakeEvents(): void {
    if (this.wakeBound || typeof document === "undefined") return;
    this.wakeBound = true;
    const wake = () => {
      if (!this.wanted) return;
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      if (!this.leader) return;
      if (!this.leader.isLeader()) return;
      if (this.source && (this.status === "open" || this.status === "connecting")) return;
      this.attempts = 0;
      this.clearRetry();
      this.closeSocket();
      this.open();
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("focus", wake);
    window.addEventListener("online", wake);
  }

  private setStatus(status: RealtimeStatus): void {
    if (this.status === status) return;
    this.status = status;
    if (this.leader?.isLeader()) {
      this.leader.publish({ kind: "status", status, lastEventId: this.lastEventId });
    }
    for (const listener of this.statusListeners) listener(status);
  }
}

function parse(raw: string): Record<string, unknown> | null {
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    // A malformed frame cannot be retried into correctness; dropping it keeps the stream alive
    // for every other event rather than taking the socket down with it.
    return null;
  }
}

export const realtime = new RealtimeClient();

/** Test seam: a fresh client with no shared state. */
export function createRealtimeClient(options: RealtimeClientOptions = {}): RealtimeClient {
  return new RealtimeClient(options);
}

export type { RealtimeClient };
